/**
 * ADR-0013 — the report builder's own API types, pinned in one file.
 *
 * The backend for this epic lives in `src/modules/reporting/` on top of the
 * `analytics/semantic` engine that `analytics.ts` already pins the meta shape
 * for. Everything below is read straight off, in order:
 *   - `reporting.controller.ts` (the routes and what each one returns)
 *   - `reporting/dto/index.ts` (what a request body may contain)
 *   - `reporting/report-definition.ts` (`ReportDefinition`, `ReportFilter`, `ReportSort`)
 *   - `reporting/report-query.service.ts` (`ReportResult`, `ReportColumn`)
 *   - `prisma/schema.prisma` (`SavedReport`, `ReportSchedule`, `ReportRecipient`,
 *     `ReportRun`, the five `Report*` enums)
 *
 * Pinned here rather than re-derived per screen so a late change to the
 * contract is a one-file fix. Nothing here is invented: every field name and
 * every enum member is copied from the source above, not guessed from a
 * response shape.
 */

// ------------------------------------------------------------------- enums

export const REPORT_OUTPUT_FORMATS = ['CSV', 'XLSX', 'JSON'] as const;
export type ReportOutputFormat = (typeof REPORT_OUTPUT_FORMATS)[number];

export const REPORT_SCHEDULE_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY'] as const;
export type ReportScheduleFrequency = (typeof REPORT_SCHEDULE_FREQUENCIES)[number];

export const REPORT_SCHEDULE_STATUSES = ['ACTIVE', 'PAUSED', 'SUSPENDED'] as const;
export type ReportScheduleStatus = (typeof REPORT_SCHEDULE_STATUSES)[number];

export const REPORT_RUN_STATUSES = [
  'PENDING',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'DELIVERED_NONE',
] as const;
export type ReportRunStatus = (typeof REPORT_RUN_STATUSES)[number];

export const REPORT_RUN_TRIGGERS = ['MANUAL', 'SCHEDULED'] as const;
export type ReportRunTrigger = (typeof REPORT_RUN_TRIGGERS)[number];

/** `reporting/date-presets.ts` — `DATE_PRESETS`. */
export const DATE_PRESETS = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
  'last_90_days',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_quarter',
  'last_quarter',
  'this_year',
  'last_year',
  'custom',
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

/** `reporting/date-presets.ts` — `COMPARISON_MODES`. */
export const COMPARISON_MODES = ['none', 'previous_period', 'previous_year'] as const;
export type ComparisonMode = (typeof COMPARISON_MODES)[number];

/** `reporting/report-definition.ts` — `REPORT_LAYOUTS`. */
export const REPORT_LAYOUTS = ['tabular', 'pivot'] as const;
export type ReportLayout = (typeof REPORT_LAYOUTS)[number];

/** `report-definition.ts` — the DTO caps (`MAX_DIMENSIONS`, `MAX_MEASURES` etc). */
export const REPORT_MAX_DIMENSIONS = 4;
export const REPORT_MAX_MEASURES = 20;
export const REPORT_MAX_FILTER_VALUES = 200;
export const REPORT_MAX_RECIPIENTS = 50;

// -------------------------------------------------------------- definition

/** The ergonomic shape the parser accepts on the way in — `{ dimension: values[] }`. */
export type ReportFiltersInput = Record<string, string[] | undefined>;

export interface ReportSort {
  /** Must be one of the report's own selected measures or dimensions. */
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * What the builder sends as `definition` — `CreateSavedReportDto.definition` /
 * `PreviewReportDto.definition`. `parseReportDefinition()` re-validates every
 * name against the registries on the way in, so a stale client-side draft
 * fails loudly at preview time rather than saving something that cannot run.
 */
export interface ReportDefinitionInput {
  measures: string[];
  dimensions: string[];
  /** Pivot column headers. Only meaningful when `layout === 'pivot'`. */
  pivotColumns: string[];
  filters: ReportFiltersInput;
  datePreset: DatePreset;
  from?: string;
  to?: string;
  comparison: ComparisonMode;
  layout: ReportLayout;
  sort?: ReportSort;
  limit: number;
}

export function blankDefinition(defaultRows: number): ReportDefinitionInput {
  return {
    measures: [],
    dimensions: [],
    pivotColumns: [],
    filters: {},
    datePreset: 'last_30_days',
    comparison: 'none',
    layout: 'tabular',
    limit: defaultRows,
  };
}

// ------------------------------------------------------------- saved report

export interface SavedReportOwner {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

export interface SavedReport {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  /** Stored JSON — re-parsed on every run, never trusted directly. */
  definition: ReportDefinitionInput;
  ownerId: string | null;
  isShared: boolean;
  createdAt: string;
  updatedAt: string;
  owner?: SavedReportOwner | null;
}

// ---------------------------------------------------------------- results

export type ReportColumnKind = 'dimension' | 'measure' | 'comparison' | 'delta' | 'deltaPct';

export interface ReportColumn {
  key: string;
  label: string;
  kind: ReportColumnKind;
  format?: string;
  pivotKey?: Array<{ dimension: string; value: string | number | null; label: string | null }>;
}

export type ReportRowValue = string | number | null;
export type ReportRow = Record<string, ReportRowValue>;

export interface ReportResult {
  layout: ReportLayout;
  timezone: string;
  window: { from: string; to: string };
  comparisonWindow: { from: string; to: string } | null;
  columns: ReportColumn[];
  rows: ReportRow[];
  rowCount: number;
  truncated: boolean;
  scope: { branchIds: string[] | null };
  requiredPermissions: string[];
}

// ------------------------------------------------------------------ runAs

export interface ReportRunAsUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
}

export interface ReportRecipientUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export interface ReportRecipient {
  id: string;
  scheduleId: string;
  userId: string;
  createdAt: string;
  user: ReportRecipientUser;
}

export interface ReportSchedule {
  id: string;
  tenantId: string;
  savedReportId: string;
  name: string;
  frequency: ReportScheduleFrequency;
  /** Wall-clock hour in `timezone`, 0-23. */
  hour: number;
  minute: number;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  timezone: string;
  format: ReportOutputFormat;
  status: ReportScheduleStatus;
  /** ADR-0013 §2.6 — whose authority every run re-derives at fire time. Immutable. */
  runAsUserId: string;
  createdById: string | null;
  linkTtlHours: number;
  nextRunAt: string;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  consecutiveFailures: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  recipients: ReportRecipient[];
  savedReport: { id: string; name: string };
  runAsUser: ReportRunAsUser;
}

export interface ReportRunArtifactMeta {
  filename: string;
  sizeBytes: number;
  mimeType: string;
  checksum: string;
}

export interface ReportRun {
  id: string;
  tenantId: string;
  savedReportId: string | null;
  scheduleId: string | null;
  trigger: ReportRunTrigger;
  status: ReportRunStatus;
  runAsUserId: string | null;
  format: ReportOutputFormat;
  scopeTenantWide: boolean;
  scopeBranchIds: string[];
  requiredPermissions: string[];
  rowCount: number | null;
  truncated: boolean;
  errorCode: string | null;
  error: string | null;
  deliveredCount: number;
  requestedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  artifact?: ReportRunArtifactMeta | null;
}
